import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createWalletClient, custom, type Abi, type Address, type Hex, type PublicClient, type WalletClient } from 'viem';

import { erc20Abi, exchangeAbi, mockUsdcAbi } from '../../../offchain/src/abi.ts';
import { addressToWord, HYPEREVM_DOMAIN } from '../../../offchain/src/cctp.ts';
import { cctpReportTypedData } from '../../../offchain/src/eip712.ts';
import {
  BRIDGE_PREVIEW_ONLY,
  burnParams,
  etaText,
  feeOn,
  GATEWAY,
  publicClientFor,
  quoteFees,
  SOURCES,
  switchWalletChain,
  TOKEN_MESSENGER,
  tokenMessengerAbi,
  USDC_DECIMALS,
  usdcAbi,
  type Fees,
  type Source,
  type Speed,
} from '../lib/cctp.ts';
import { config, deployment } from '../config.ts';
import { deadlineIn, newNonce, run, signTyped } from '../lib/intents.ts';
import { amountInput, fmtUnits, fmtUsd, parseUnits, shortAddr, toInput } from '../lib/format.ts';
import { relayer } from '../lib/relayer.ts';
import { errorMessage, sendTx } from '../lib/tx.ts';
import { holdChain } from '../lib/wallet.ts';
import { useApp } from '../state.tsx';
import { TransfersTable } from './Tables.tsx';
import { copyText, Row, useDialogFocus, useEscape } from './ui.tsx';

export function FundsModal() {
  const { funds, openFunds, wallet } = useApp();
  if (!funds || !wallet) return null;
  return <FundsDialog />;
}

function FundsDialog() {
  const { funds, openFunds } = useApp();
  useEscape(useCallback(() => openFunds(undefined), [openFunds]));
  const dialog = useDialogFocus();
  return createPortal(
    <div className="scrim" onClick={() => openFunds(undefined)}>
      <div className="modal" ref={dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={funds}>
        <header className="modal-head">
          <div className="seg">
            <button className={funds === 'deposit' ? 'is-on' : ''} onClick={() => openFunds('deposit')}>
              Deposit
            </button>
            <button className={funds === 'withdraw' ? 'is-on' : ''} onClick={() => openFunds('withdraw')}>
              Withdraw
            </button>
          </div>
          <button className="x" onClick={() => openFunds(undefined)} aria-label="Close">
            ×
          </button>
        </header>
        {funds === 'deposit' ? <Deposit /> : <Withdraw />}
        <div className="modal-recent">
          <h4>Recent</h4>
          <TransfersTable limit={4} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

type DepositRoute = 'core' | 'evm' | 'bridge';

function Deposit() {
  const { user, globals, wallet, toast, refresh, openFunds } = useApp();
  const [route, setRoute] = useState<DepositRoute>('evm');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string>();
  const [coreAddr, setCoreAddr] = useState<string>();
  // the route exists where the deployment has a gateway; the local chain shows it without running it
  const bridge = !!GATEWAY || BRIDGE_PREVIEW_ONLY;

  useEffect(() => {
    if (route !== 'core' || !wallet) return;
    // asking for the address also makes the keeper watch it
    relayer.depositAddress(wallet.address).then(setCoreAddr, (e) => setCoreAddr(`unavailable: ${errorMessage(e)}`));
  }, [route, wallet]);

  if (!globals || !wallet) return null;
  const sym = globals.collateralSymbol;
  const amount = parseUnits(text, globals.collateralDecimals);

  async function deposit() {
    if (!amount || !user || !wallet) return;
    try {
      if (user.collateralAllowance < amount) {
        setBusy('Approving…');
        await sendTx(wallet.client, { address: globals!.collateral, abi: erc20Abi, functionName: 'approve', args: [deployment.exchange, amount] });
      }
      setBusy('Depositing…');
      await sendTx(wallet.client, { address: deployment.exchange, abi: exchangeAbi, functionName: 'deposit', args: [amount] });
      toast({ kind: 'ok', title: 'Deposit credited', body: `${text} ${sym}` });
      refresh();
      openFunds(undefined);
    } catch (e) {
      toast({ kind: 'err', title: 'Deposit failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  async function faucet() {
    if (!wallet || !deployment.usdc) return;
    setBusy('Minting…');
    try {
      await sendTx(wallet.client, {
        address: deployment.usdc,
        abi: mockUsdcAbi,
        functionName: 'mint',
        args: [wallet.address, 10_000n * 10n ** BigInt(globals!.collateralDecimals)],
      });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Faucet failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div className="modal-body">
      <div className="route-switch">
        <button className={route === 'evm' ? 'is-on' : ''} onClick={() => setRoute('evm')}>
          From HyperEVM
          <small>wallet {sym}</small>
        </button>
        <button className={route === 'core' ? 'is-on' : ''} onClick={() => setRoute('core')}>
          From HyperCore
          <small>no gas</small>
        </button>
        {bridge && (
          <button className={route === 'bridge' ? 'is-on' : ''} onClick={() => setRoute('bridge')}>
            From another chain
            <small>USDC via Circle</small>
          </button>
        )}
      </div>
      {route === 'core' && (
        <>
          <p className="desc">
            Send {sym} on HyperCore spot to your deposit address. It is credited to your trading balance within a few
            seconds; amounts under 1 {sym} wait until the total reaches it. Your first send to this address costs 1 {sym}{' '}
            more: Hyperliquid charges it to the sender to open the address&apos;s account, and the full amount you send
            arrives.
          </p>
          <button
            className="addr num"
            title="Copy"
            disabled={!coreAddr || coreAddr.startsWith('unavailable')}
            onClick={() => coreAddr && copyText(coreAddr).then((ok) => toast({ kind: 'info', title: ok ? 'Address copied' : 'Copy it by hand: no clipboard here' }))}
          >
            {coreAddr ?? 'Fetching…'}
            <span className="muted small">copy</span>
          </button>
        </>
      )}
      {route === 'evm' && (
        <>
          <Row label="Wallet balance">{user ? `${fmtUnits(user.walletCollateral, globals.collateralDecimals, 2)} ${sym}` : '- -'}</Row>
          <div className="field">
            <input className="num" inputMode="decimal" placeholder="0.00" value={text} onChange={(e) => setText(amountInput(e.target.value))} />
            <button className="chip" onClick={() => user && setText(toInput(user.walletCollateral, globals.collateralDecimals))}>
              Max
            </button>
          </div>
          <button className="btn btn-primary btn-block" disabled={!!busy || !amount || !user || amount > user.walletCollateral} onClick={deposit}>
            {busy ?? (user && amount && user.collateralAllowance < amount ? 'Approve & Deposit' : 'Deposit')}
          </button>
          {config.isLocal && deployment.usdc && (
            <button className="btn btn-ghost btn-block" disabled={!!busy} onClick={faucet}>
              Local faucet: mint 10,000 test {sym}
            </button>
          )}
        </>
      )}
      {route === 'bridge' && <Bridge />}
    </div>
  );
}

/** Sends a transaction on a source chain: simulated and awaited through that chain's own client */
async function sendOn(
  wallet: WalletClient,
  reader: PublicClient,
  c: { address: Address; abi: Abi | readonly unknown[]; functionName: string; args: unknown[] },
): Promise<Hex> {
  const { request } = await reader.simulateContract({ account: wallet.account!, ...c } as never);
  const hash = await wallet.writeContract(request as never);
  const r = await reader.waitForTransactionReceipt({ hash });
  if (r.status !== 'success') throw new Error(`${c.functionName} reverted (${hash})`);
  return hash;
}

/**
 * A deposit from another chain: the wallet moves to it, approves USDC to Circle's TokenMessengerV2 and burns it
 * naming the gateway (mint recipient and destination caller) and the user (hook data); the burn is reported to the
 * API, whose keeper finalizes it here once Circle attests it. The user needs gas on the source chain only.
 */
function Bridge() {
  const { globals, wallet, signer, toast, refresh, openFunds, setWallet } = useApp();
  const [src, setSrc] = useState<Source>(SOURCES[0]!);
  const [text, setText] = useState('');
  const [speed, setSpeed] = useState<Speed>('fast');
  const [fees, setFees] = useState<Fees>();
  const [feesError, setFeesError] = useState<string>();
  const [balance, setBalance] = useState<bigint>();
  const [balanceError, setBalanceError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  // bumped by Retry: the quote and the balance are read again
  const [attempt, setAttempt] = useState(0);
  const provider = wallet?.provider;
  const preview = BRIDGE_PREVIEW_ONLY || !provider || !GATEWAY;

  useEffect(() => {
    setFees(undefined);
    setFeesError(undefined);
    setBalance(undefined);
    setBalanceError(undefined);
    if (BRIDGE_PREVIEW_ONLY || !wallet) return;
    let gone = false;
    quoteFees(src).then(
      (f) => !gone && setFees(f),
      (e) => !gone && setFeesError(errorMessage(e)),
    );
    publicClientFor(src)
      .readContract({ address: src.usdc, abi: usdcAbi, functionName: 'balanceOf', args: [wallet.address] })
      .then(
        (b) => !gone && setBalance(b),
        (e) => !gone && setBalanceError(errorMessage(e)),
      );
    return () => {
      gone = true;
    };
  }, [src, wallet, attempt]);

  if (!globals || !wallet) return null;
  const amount = parseUnits(text, USDC_DECIMALS);
  const bps = fees ? (speed === 'fast' ? fees.fastBps : fees.standardBps) : undefined;
  const fee = amount !== undefined && bps !== undefined ? feeOn(amount, bps) : undefined;
  const scale = 10n ** BigInt(18 - globals.collateralDecimals);

  async function bridge() {
    if (!amount || !wallet || !provider || !GATEWAY || !fees || !signer) return;
    const gateway = GATEWAY;
    let hash: Hex | undefined;
    try {
      await holdChain(async () => {
        try {
          setBusy(`Switching to ${src.name}…`);
          await switchWalletChain(provider, src.chain);
          const client = createWalletClient({ account: wallet.address, chain: src.chain, transport: custom(provider) });
          const reader = publicClientFor(src);
          const allowance = await reader.readContract({ address: src.usdc, abi: usdcAbi, functionName: 'allowance', args: [wallet.address, TOKEN_MESSENGER] });
          if (allowance < amount) {
            setBusy('Approving USDC…');
            await sendOn(client, reader, { address: src.usdc, abi: usdcAbi, functionName: 'approve', args: [TOKEN_MESSENGER, amount] });
          }
          setBusy(`Burning on ${src.name}…`);
          const { maxFee, minFinalityThreshold } = burnParams(amount, speed, fees);
          hash = await sendOn(client, reader, {
            address: TOKEN_MESSENGER,
            abi: tokenMessengerAbi,
            functionName: 'depositForBurnWithHook',
            args: [amount, HYPEREVM_DOMAIN, addressToWord(gateway), src.usdc, addressToWord(gateway), maxFee, minFinalityThreshold, addressToWord(wallet.address)],
          });
        } finally {
          // whatever happened over there (a refusal included), the wallet comes back before the app looks at it
          setBusy('Back to HyperEVM…');
          await switchWalletChain(provider, config.chain).catch(() => {});
        }
      });
      if (Number(await provider.request({ method: 'eth_chainId' })) !== config.chainId) {
        toast({ kind: 'info', title: 'Switch your wallet back to HyperEVM to keep trading' });
        setWallet(undefined);
      }
      if (!hash) return;
      setBusy('Reporting the burn…');
      // signed by the user (a session key signs silently); the keeper follows it from here
      const burn = hash;
      await relayer
        .reportCctpDeposit({
          sourceDomain: src.domain,
          txHash: burn,
          user: wallet.address,
          amount: amount * scale,
          signature: await signTyped(cctpReportTypedData(src.domain, burn, deployment, config.chainId), signer),
        })
        .catch((e) => toast({ kind: 'err', title: 'Burn sent, but the relayer did not take it', body: `${errorMessage(e)}. Keep the transaction hash: ${burn}` }));
      toast({ kind: 'ok', title: `Bridging ${text} USDC from ${src.name}`, body: `Credited to your trading balance in ${etaText(src, speed)}.` });
      refresh();
      openFunds(undefined);
    } catch (e) {
      toast({ kind: 'err', title: 'Deposit from another chain failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <>
      <p className="desc">
        USDC burned on the chain you pick through Circle's CCTP and credited here once Circle attests it. You pay gas
        there, no HYPE needed.
      </p>
      <div className="field">
        <select value={src.domain} onChange={(e) => setSrc(SOURCES.find((s) => s.domain === Number(e.target.value)) ?? SOURCES[0]!)} disabled={!!busy}>
          {SOURCES.map((s) => (
            <option key={s.domain} value={s.domain}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <Row label={`USDC on ${src.name}`}>
        {balance !== undefined ? `${fmtUnits(balance, USDC_DECIMALS, 2)} USDC` : preview ? '- -' : balanceError ? 'unknown' : '…'}
      </Row>
      {(balanceError || feesError) && !busy && (
        <p className="desc">
          {balanceError ? `${src.name} did not answer: ${balanceError}. ` : ''}
          {feesError ? `Circle's fee quote failed: ${feesError}. ` : ''}
          <button className="link" onClick={() => setAttempt((a) => a + 1)}>
            Retry
          </button>
        </p>
      )}
      <div className="field">
        <input className="num" inputMode="decimal" placeholder="0.00" value={text} onChange={(e) => setText(amountInput(e.target.value))} disabled={!!busy} />
        <button className="chip" disabled={balance === undefined || !!busy} onClick={() => balance !== undefined && setText(toInput(balance, USDC_DECIMALS))}>
          Max
        </button>
      </div>
      <div className="speed">
        <button className={`chip ${speed === 'fast' ? 'is-on' : ''}`} onClick={() => setSpeed('fast')} disabled={!!busy}>
          Fast · {etaText(src, 'fast')}
          <small>{fees ? `${fees.fastBps} bp fee` : 'small fee'}</small>
        </button>
        <button className={`chip ${speed === 'standard' ? 'is-on' : ''}`} onClick={() => setSpeed('standard')} disabled={!!busy}>
          Standard · {etaText(src, 'standard')}
          <small>{fees && fees.standardBps > 0 ? `${fees.standardBps} bp fee` : 'free'}</small>
        </button>
      </div>
      <Row label="Circle fee">{bps === undefined ? (feesError ? 'unavailable' : preview ? '- -' : '…') : fee !== undefined ? `${fmtUnits(fee, USDC_DECIMALS, 4)} USDC` : `${bps} bp`}</Row>
      <Row label="You receive">{amount !== undefined && fee !== undefined ? `$${fmtUsd((amount - fee) * scale)}` : '- -'}</Row>
      <button
        className="btn btn-primary btn-block"
        disabled={preview || !!busy || !amount || !fees || (balance !== undefined && amount > balance)}
        onClick={bridge}
      >
        {busy ?? `Deposit from ${src.name}`}
      </button>
      {BRIDGE_PREVIEW_ONLY && <p className="desc">Not on this local chain: its gateway talks to a stand-in, a real burn would never land here.</p>}
      {!BRIDGE_PREVIEW_ONLY && !provider && <p className="desc">Needs a browser wallet that can switch chains.</p>}
      {!preview && (
        <p className="desc">
          Your wallet switches to {src.name}, approves USDC to Circle's TokenMessenger and burns it; then it switches back. Keep this
          tab open until it does. The deposit shows as <i>Bridging</i> below until it lands. {shortAddr(TOKEN_MESSENGER)} is Circle's
          TokenMessenger.
        </p>
      )}
    </>
  );
}

function Withdraw() {
  const { user, globals, wallet, signer, toast, refresh, openFunds } = useApp();
  const [route, setRoute] = useState<'core' | 'evm'>('evm');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  if (!globals || !wallet) return null;
  const amount = parseUnits(text, 18);
  const noCore = route === 'core' && user && !user.coreUser;
  // each route pays from its own half of the exchange's funds; the keeper refills a half within minutes
  const routeCap = route === 'core' ? globals.poolCore : globals.poolEvm;
  const otherCap = route === 'core' ? globals.poolEvm : globals.poolCore;
  const overRoute = routeCap !== undefined && !!amount && amount > routeCap;
  const available = user ? (routeCap !== undefined && routeCap < user.balance ? routeCap : user.balance) : undefined;

  async function withdraw() {
    if (!amount || !wallet || !signer) return;
    setBusy(true);
    try {
      if (route === 'core') {
        await run({ action: 'withdrawToCore', request: { user: wallet.address, amount, nonce: newNonce(), deadline: deadlineIn() } }, signer);
      } else {
        await sendTx(wallet.client, { address: deployment.exchange, abi: exchangeAbi, functionName: 'withdraw', args: [amount] });
      }
      toast({ kind: 'ok', title: route === 'core' ? 'Sent to HyperCore' : 'Sent to your wallet', body: `$${text}` });
      refresh();
      openFunds(undefined);
    } catch (e) {
      toast({ kind: 'err', title: 'Withdrawal failed', body: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-body">
      <div className="route-switch">
        <button className={route === 'evm' ? 'is-on' : ''} onClick={() => setRoute('evm')}>
          To HyperEVM
          <small>{shortAddr(wallet.address)}</small>
        </button>
        <button className={route === 'core' ? 'is-on' : ''} onClick={() => setRoute('core')}>
          To HyperCore
          <small>no gas</small>
        </button>
      </div>
      <Row label="Your balance">{user ? `$${fmtUsd(user.balance)}` : '- -'}</Row>
      {user && available !== undefined && available < user.balance && (
        <Row label={route === 'core' ? 'Payable to HyperCore now' : 'Payable to HyperEVM now'}>${fmtUsd(available)}</Row>
      )}
      <div className="field">
        <input className="num" inputMode="decimal" placeholder="0.00" value={text} onChange={(e) => setText(amountInput(e.target.value))} />
        <button className="chip" onClick={() => available !== undefined && setText(toInput(available, 18))}>
          Max
        </button>
      </div>
      <button className="btn btn-primary btn-block" disabled={busy || !amount || !user || amount > user.balance || overRoute || !!noCore} onClick={withdraw}>
        {busy ? 'Withdrawing…' : 'Withdraw'}
      </button>
      {overRoute && (
        <p className="desc">
          The exchange holds ${fmtUsd(routeCap!)} {route === 'core' ? 'on HyperCore' : 'on HyperEVM'} right now
          {otherCap !== undefined && otherCap > 0n ? ` and $${fmtUsd(otherCap)} ${route === 'core' ? 'on HyperEVM' : 'on HyperCore'}` : ''}. Withdraw
          less, use the other route, or try again in a few minutes: the keeper moves funds between the two.
        </p>
      )}
      {noCore && <p className="desc">This address has no HyperCore account yet. Activate it on Hyperliquid, or withdraw to HyperEVM.</p>}
      {route === 'evm' && <p className="desc">A wallet transaction: it needs a little HYPE for gas.</p>}
    </div>
  );
}
